import { LEGACY_APOLLO_FILTERS } from "@signalist/shared-types";

import { IFilterWithValues } from "../../../components/FilterModal";

type FilterRow = IFilterWithValues[string];
type Option = { id: string; title: string };

const TEXT_AND_NUMBER_TYPES = [
	"textContains",
	"textDoesNotContain",
	"textStartsWith",
	"textEndsWith",
	"textIsExactly",
	"greaterEqual",
	"lessEqual",
];

const isEmptyList = (value: unknown) => Array.isArray(value) && value.length === 0;

/** A row the user left empty, which the step does not save. */
function isUnset(row: FilterRow): boolean {
	const { type, value } = row;

	if (["dropdownCustom", "dropdownCustomExclude", "dropdown", "dropdownExclude"].includes(type)) {
		return isEmptyList(value);
	}

	if (TEXT_AND_NUMBER_TYPES.includes(type)) {
		return !value;
	}

	if (type === "between") {
		// PREC-4622: one bound is a range with an open end ("10001 or more"); it used to be dropped
		// on save, which deleted a legacy open range the step had just shown.
		const bounds = value as { from?: unknown; till?: unknown } | undefined;

		return !bounds?.from && !bounds?.till;
	}

	return false;
}

/**
 * The legacy key a dropdown row is written back to (person_locations for the "country" row, …),
 * from the shared table the API reads it with.
 */
function legacyArrayKeyOf(rowName: string, row: FilterRow): string | undefined {
	if (row.type !== "dropdown") {
		return undefined;
	}

	return Object.entries(LEGACY_APOLLO_FILTERS).find(
		([, legacy]) =>
			legacy.filterName === rowName &&
			(legacy.valueSource === "valueOrSelf" || legacy.valueSource === "self")
	)?.[0];
}

/**
 * The SOURCE apollo config the Filter step saves (PREC-4622, extracted from ListEditFilter):
 * the stored waterfall and cap, every filter row the user filled in, and the filters it could not
 * read and the user did not remove (`unreadable`), which a row of the same name replaces. The
 * API keeps every other stored setting (mergeApolloConfigUpdate).
 */
export function buildFilterStepConfig(
	rows: IFilterWithValues | undefined,
	stored: { enrichmentFilterConfig?: unknown; maxPerCompany?: unknown },
	unreadable: Record<string, unknown>
): Record<string, unknown> {
	const config: Record<string, unknown> = {
		...unreadable,
		enrichmentFilterConfig: stored.enrichmentFilterConfig,
		maxPerCompany: stored.maxPerCompany,
	};

	for (const [name, row] of Object.entries(rows ?? {})) {
		if (name === "maxPerCompany" || isUnset(row)) {
			continue;
		}

		const legacyKey = legacyArrayKeyOf(name, row);

		if (legacyKey) {
			config[legacyKey] = (row.value as Option[]).map((option) => option.id);
		} else {
			config[name.replaceAll(" ", "_")] = {
				type: row.type,
				value: row.value,
				filterParam: row.filterParam,
				customColumnId: row.customColumnId,
			};
		}
	}

	return { ...config, firstSaved: true };
}
